/**
 * Tenant-scoped company directory + unified Employer CRUD.
 *
 * `GET /employment/v1/employers` returns every company under the current
 * tenant; the company switcher intersects this list with the user's admin
 * scope (`custom:administered_companies`, or all when `custom:role` is
 * `TenantAdmin`).
 *
 * **V2 (slice W2).** These calls moved off the legacy `/employment-svc`
 * host onto the V2 employment service (`/employment/v1`, routed by
 * `src/api/apiHosts.ts`). The wire field names are unchanged — the V2 contract
 * (`external/openapi-employment-v2.yaml`, schemas `Employer` / `EmployerDetails`)
 * answers in today's PascalCase vocabulary verbatim, misspelled
 * `PhnoneNumbers` included — so every normaliser below is untouched. Two
 * differences the service absorbs so no component changes:
 *   - the list answers the shared `{Items, Count}` envelope;
 *   - `EmployerRequest` is `additionalProperties: false` and has no
 *     `BaseLocation`, so that key is stripped before the write (see
 *     `stripUnsupportedRequestKeys`).
 *
 * v2 staff-outsourcing (OUTSOURCING_IMPLEMENTATION_PLAN.md) generalises on
 * the single `Employer` entity. An Employer now carries a `Kind`
 * discriminator (`COMPANY | OS_CLIENT | OS_VENDOR | OS_PROJECT`) + a
 * `ParentId` (project→its OS_CLIENT/OS_VENDOR; subsidiary→parent). This module
 * is the single wire→FE normalisation boundary for the Employer shape,
 * including those v2 fields. (`EngagementType` + `LegalName` were removed from
 * the model in sibling BE PR #424.)
 *
 * The endpoints live in the **employment service**, NOT the payroll service,
 * so they are outside `external/openapi-payroll.yaml`. We type the wire shape
 * (PascalCase) locally and normalise once here to the camelCase shape the
 * rest of the frontend consumes.
 */
import { httpClient } from '@/api/httpClient';
import { warnOnce } from '@/api/warnOnce';

// ---------------------------------------------------------------------------
// Wire shapes (PascalCase — as the employment service ships them)
// ---------------------------------------------------------------------------

/**
 * Wire shape captured against dev (2026-05-13, extended for v2 2026-06-16):
 * the backend returns a nested `Organization` object and uses `EntityItemId`
 * rather than `EmployerId`.
 *
 * `Type` values observed so far:
 *   - `TENANT_EMPLOYER` — the tenant's main company (the switcher defaults
 *     tenant-wide admins to this entry).
 *   - `MANAGED_EMPLOYER` — any company created after the first one.
 *   - any other string → mapped to `UNKNOWN` (forwards-compatible).
 */
export interface EmployerWire {
  EntityItemId: string;
  Organization?: {
    ShortName?: string;
    FullName?: string;
  };
  TenantId?: string;
  Type?: 'TENANT_EMPLOYER' | 'MANAGED_EMPLOYER' | string;
  Kind?: 'COMPANY' | 'OS_CLIENT' | 'OS_VENDOR' | 'OS_PROJECT' | string;
  ParentId?: string;
  ProjectName?: string;
  Industry?: string;
  EmployeeIdFormat?: string;
  Website?: string;
  Emails?: string[];
  PhnoneNumbers?: string[];
  Representatives?: string[];
  SocialNetworkProfiles?: string[];
  Address?: string;
  DetailedAddress?: string;
  Notes?: string;
  PrimaryContact?: {
    Name?: string;
    Email?: string;
    Phone?: string;
  };
  CreatedBy?: string;
  CreatedAt?: string;
}

/** A single policy/setting property as the enriched GET-employer ships it. */
export interface PolicyPropertyWire {
  Label?: string;
  Description?: string;
  Value?: unknown;
  Implemented?: boolean;
  Validation?: {
    Type?: 'string' | 'number' | 'integer' | 'boolean' | 'enum' | 'range' | string;
    Min?: number;
    Max?: number;
    Step?: number;
    Options?: { Label?: string; Value?: unknown }[];
  };
}

/** One module's policy object: `{ Id, <Key>: PolicyPropertyWire, ... }`. */
export type PolicyModuleWire = { Id?: string } & Record<string, PolicyPropertyWire | string>;

/**
 * The enriched `GET /v2/employers/{id}` (`EmployerDetails`) adds `Policies`
 * (Attendance/Leave/Employment/OT/Schedule) + `Settings` (Localization, …) to
 * the base Employer wire shape.
 */
export interface EmployerDetailsWire extends EmployerWire {
  Policies?: Record<string, PolicyModuleWire>;
  Settings?: Record<string, PolicyModuleWire>;
}

/**
 * Some backends wrap the list (`{ Employers: [...] }`); others return the bare
 * array. We accept both at the boundary.
 */
type EmployersListWire =
  | EmployerWire[]
  | { Items?: EmployerWire[]; Employers?: EmployerWire[]; Count?: number };

// ---------------------------------------------------------------------------
// FE-facing shapes (camelCase)
// ---------------------------------------------------------------------------

export type EmployerType = 'TENANT_EMPLOYER' | 'EMPLOYER' | 'UNKNOWN';
export type EmployerKind = 'COMPANY' | 'OS_CLIENT' | 'OS_VENDOR' | 'OS_PROJECT' | 'UNKNOWN';

export interface EmployerContact {
  name: string;
  email: string;
  phone: string;
}

export interface Employer {
  /** Full EntityItemId, e.g. "Employer:xxx". Route param + lookup key. */
  id: string;
  name: string;
  shortName: string;
  status: 'ACTIVE' | 'INACTIVE' | 'UNKNOWN';
  type: EmployerType;
  kind: EmployerKind;
  parentId: string | null;
  projectName: string;
  industry: string;
  employeeIdFormat: string;
  website: string;
  emails: string[];
  phoneNumbers: string[];
  representatives: string[];
  socialNetworkProfiles: string[];
  address: string;
  notes: string;
  primaryContact: EmployerContact;
}

/** A normalised policy/setting property for the editor UI. */
export interface PolicyProperty {
  key: string;
  label: string;
  description: string;
  value: unknown;
  validationType: 'string' | 'number' | 'integer' | 'boolean' | 'enum' | 'range' | 'object';
  options: { label: string; value: unknown }[];
  min: number | null;
  max: number | null;
  step: number | null;
}

/** A normalised policy/setting module group for the accordion. */
export interface PolicyModule {
  /** Module name e.g. "Attendance" / "Localization". */
  name: string;
  /** The Policy/Setting EntityItemId used as `policySettingId` on the PUT. */
  id: string;
  properties: PolicyProperty[];
}

export interface EmployerDetails extends Employer {
  policies: PolicyModule[];
  settings: PolicyModule[];
}

/**
 * Request body for create / update. The BE always persists
 * `Organization.{FullName,ShortName}`; the rest are optional discriminators.
 * (`LegalName` was removed from the model in BE PR #424.)
 */
export interface EmployerRequest {
  Organization: { FullName: string; ShortName?: string };
  Kind?: Exclude<EmployerKind, 'UNKNOWN'>;
  ParentId?: string;
  ProjectName?: string;
  Industry?: string;
  EmployeeIdFormat?: string;
  Website?: string;
  Emails?: string[];
  PhoneNumbers?: string[];
  Representatives?: string[];
  Address?: string;
  Notes?: string;
  PrimaryContact?: { Name?: string; Email?: string; Phone?: string };
  /**
   * The company's primary base location, sent ONLY on COMPANY create (mirrors
   * the v1 "Create New Company" form, which sets `dataEmployer.BaseLocation`
   * from the map picker). The wire shape is verbatim from v1's `Map.vue`
   * `submitMap` body — `{ Name, Code, Status, Type, Location:{Lat,Long as
   * strings}, DetailedAddress, Country?/Province?/District? }`. The v2 picker
   * fills `Name`, `Location.{Lat,Long}` (from a Leaflet pin) and
   * `DetailedAddress`; the SRM-backed Country/Province/District dropdowns are
   * left for a follow-up so the picker needs no extra API. The BE persists the
   * location server-side; omit it for non-COMPANY kinds and on edit.
   */
  BaseLocation?: BaseLocation;
}

/**
 * The structured base-location wire shape, matching v1 `Map.vue`'s `submitMap`
 * body exactly (the keys employment-svc reads off `Employer.BaseLocation`).
 * `Location.{Lat,Long}` are STRINGS on the wire (v1 sends
 * `marker.lat.toString()`), so the picker stringifies its numeric pin.
 */
export interface BaseLocation {
  /** Human-readable location name, e.g. "Head Office". Required. */
  Name: string;
  /** Optional company-specified location code. */
  Code?: string;
  /** Lifecycle status — defaults to "Active" (v1 default). */
  Status?: string;
  /** "Primary" for the main company location (v1 default on create). */
  Type?: string;
  /** Decimal-degree coordinates as STRINGS (v1 wire shape). */
  Location: { Lat: string; Long: string };
  /** Free-text street/building address. */
  DetailedAddress?: string;
  /** Optional SRM-referenced administrative areas (left unset by the v2 picker). */
  Country?: { Id?: string; Name?: string };
  Province?: { Id?: string; Name?: string };
  District?: { Id?: string; Name?: string };
}

// ---------------------------------------------------------------------------
// Normalisers (the single wire→FE boundary)
// ---------------------------------------------------------------------------

export function normalizeEmployerType(raw: string | undefined): EmployerType {
  if (raw === 'TENANT_EMPLOYER') return 'TENANT_EMPLOYER';
  if (raw === 'MANAGED_EMPLOYER' || raw === 'EMPLOYER') return 'EMPLOYER';
  return 'UNKNOWN';
}

export function normalizeEmployerKind(raw: string | undefined): EmployerKind {
  switch (raw) {
    case 'COMPANY':
      return 'COMPANY';
    case 'OS_CLIENT':
      return 'OS_CLIENT';
    case 'OS_VENDOR':
      return 'OS_VENDOR';
    case 'OS_PROJECT':
      return 'OS_PROJECT';
    case undefined:
    case '':
      // Absent Kind defaults to COMPANY — every v2-created Employer sets one,
      // so a missing value is a pre-v2 record (an ordinary company).
      return 'COMPANY';
    default:
      warnOnce(`Unknown Employer.Kind: ${raw}`);
      return 'UNKNOWN';
  }
}

/**
 * The best human-readable label for an employer, used wherever a single
 * display string is needed (switcher rows, chip face). Falls back through
 * `shortName → name → projectName → the local part of the EntityItemId`, so
 * the UI never renders an empty string or a `'?'` placeholder.
 */
export function employerLabel(
  e: Pick<Employer, 'shortName' | 'name' | 'projectName' | 'id'>,
): string {
  const candidate =
    e.shortName?.trim() || e.name?.trim() || e.projectName?.trim() || idLocalPart(e.id);
  return candidate || 'Scope';
}

/**
 * A robust 1–4 char initial/badge for an employer. Derives from the resolved
 * label: an acronym from multi-word names (e.g. "Optimal P" → "OP"), else the
 * first 1–2 letters. Never returns `'?'`.
 */
export function employerInitial(
  e: Pick<Employer, 'shortName' | 'name' | 'projectName' | 'id'>,
): string {
  const label = employerLabel(e);
  const words = label.split(/[\s·\-_/]+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0]!.charAt(0) + words[1]!.charAt(0)).toUpperCase();
  }
  return label.slice(0, 2).toUpperCase() || 'S';
}

/**
 * The FULL company/client/vendor/project name for display (the primary switcher
 * row label). Falls back through the short name / id local-part so it never
 * renders a raw `Employer:<uuid>` or an empty string.
 */
export function employerFullName(
  e: Pick<Employer, 'shortName' | 'name' | 'projectName' | 'id'>,
): string {
  const full = e.name?.trim();
  if (full && full !== e.id) return full;
  return employerLabel(e);
}

/**
 * The short-name badge shown in muted grey beneath the full name. Empty when the
 * short name is missing or identical to the full name (no point repeating it).
 */
export function employerShortBadge(
  e: Pick<Employer, 'shortName' | 'name' | 'projectName' | 'id'>,
): string {
  const short = e.shortName?.trim();
  return short && short !== employerFullName(e) ? short : '';
}

/** The id portion after the `Entity:` prefix (e.g. "Employer:abc" → "abc"). */
function idLocalPart(id: string | undefined): string {
  if (!id) return '';
  const idx = id.lastIndexOf(':');
  const tail = idx >= 0 ? id.slice(idx + 1) : id;
  return tail.slice(0, 6);
}

export function normalizeEmployer(wire: EmployerWire): Employer {
  const fullName = wire.Organization?.FullName?.trim();
  const shortName = wire.Organization?.ShortName?.trim();
  // Fall back through ShortName → FullName → the raw EntityItemId so the chip
  // never renders an empty string for the option.
  const display = shortName || fullName || wire.EntityItemId;
  const contact = wire.PrimaryContact ?? {};
  return {
    id: wire.EntityItemId,
    name: fullName || shortName || wire.EntityItemId,
    shortName: display,
    status: 'ACTIVE',
    type: normalizeEmployerType(wire.Type),
    kind: normalizeEmployerKind(wire.Kind),
    parentId: wire.ParentId?.trim() || null,
    projectName: wire.ProjectName?.trim() ?? '',
    industry: wire.Industry?.trim() ?? '',
    employeeIdFormat: wire.EmployeeIdFormat?.trim() ?? '',
    website: wire.Website?.trim() ?? '',
    emails: (wire.Emails ?? []).filter((e): e is string => Boolean(e?.trim())),
    phoneNumbers: (wire.PhnoneNumbers ?? []).filter((p): p is string => Boolean(p?.trim())),
    representatives: (wire.Representatives ?? []).filter((r): r is string => Boolean(r?.trim())),
    socialNetworkProfiles: (wire.SocialNetworkProfiles ?? []).filter((s): s is string =>
      Boolean(s?.trim()),
    ),
    address: (wire.Address || wire.DetailedAddress)?.trim() ?? '',
    notes: wire.Notes?.trim() ?? '',
    primaryContact: {
      name: contact.Name?.trim() ?? '',
      email: contact.Email?.trim() ?? '',
      phone: contact.Phone?.trim() ?? '',
    },
  };
}

/** Resolve a property's editor type, defaulting from the runtime value. */
function resolveValidationType(prop: PolicyPropertyWire): PolicyProperty['validationType'] {
  const declared = prop.Validation?.Type;
  if (
    declared === 'string' ||
    declared === 'number' ||
    declared === 'integer' ||
    declared === 'boolean' ||
    declared === 'enum' ||
    declared === 'range'
  ) {
    return declared;
  }
  const v = prop.Value;
  if (typeof v === 'boolean') return 'boolean';
  if (typeof v === 'number') return 'number';
  if (typeof v === 'string') return 'string';
  return 'object';
}

function normalizePolicyModule(name: string, mod: PolicyModuleWire): PolicyModule {
  const id = typeof mod.Id === 'string' ? mod.Id : '';
  const properties: PolicyProperty[] = [];
  for (const [key, raw] of Object.entries(mod)) {
    if (key === 'Id') continue;
    if (!raw || typeof raw === 'string') continue;
    const prop = raw as PolicyPropertyWire;
    // Hide explicitly un-implemented keys (forwards-compat with BE rollout).
    if (prop.Implemented === false) continue;
    properties.push({
      key,
      label: prop.Label?.trim() || key.replace(/([A-Z])/g, ' $1').trim(),
      description: prop.Description?.trim() ?? '',
      value: prop.Value,
      validationType: resolveValidationType(prop),
      options: (prop.Validation?.Options ?? []).map((o) => ({
        label: o.Label ?? String(o.Value ?? ''),
        value: o.Value,
      })),
      min: typeof prop.Validation?.Min === 'number' ? prop.Validation.Min : null,
      max: typeof prop.Validation?.Max === 'number' ? prop.Validation.Max : null,
      step: typeof prop.Validation?.Step === 'number' ? prop.Validation.Step : null,
    });
  }
  return { name, id, properties };
}

function normalizePolicyGroups(
  groups: Record<string, PolicyModuleWire> | undefined,
): PolicyModule[] {
  if (!groups) return [];
  return Object.entries(groups)
    .map(([name, mod]) => normalizePolicyModule(name, mod))
    .filter((m) => m.properties.length > 0);
}

export function normalizeEmployerDetails(wire: EmployerDetailsWire): EmployerDetails {
  return {
    ...normalizeEmployer(wire),
    policies: normalizePolicyGroups(wire.Policies),
    settings: normalizePolicyGroups(wire.Settings),
  };
}

// ---------------------------------------------------------------------------
// V2 routes
// ---------------------------------------------------------------------------

/**
 * The V2 employment service's employer collection. The per-employer URLs are
 * written out in full at every call site on purpose:
 * `scripts/openapi-sync-check.ts` reads the literal template strings and
 * asserts each one against the contract, which a helper-composed prefix would
 * hide from it.
 */
export const EMPLOYERS_URL = '/employment/v1/employers';

/**
 * Path-encode one identifier.
 * @param value the identifier to encode.
 * @returns the encoded segment.
 */
function seg(value: string): string {
  return encodeURIComponent(value);
}

/**
 * Keys the FE request shape carries that the V2 `EmployerRequest` schema does
 * not accept. The schema is `additionalProperties: false`, so leaving them in
 * would fail the whole write with a 400 rather than being ignored.
 *
 * `BaseLocation` is the only one: V1's create-company form posted the map pin
 * inline, V2 creates the employer (with its counters, layer config and seeded
 * policies) and nothing else. Base locations get their own route in slice W2b,
 * so the key is dropped here and the caller's signature is unchanged.
 * @param body the request body as the caller built it.
 * @returns the same body with the unsupported keys removed.
 */
export function stripUnsupportedRequestKeys(body: EmployerRequest): EmployerRequest {
  if (!('BaseLocation' in body)) return body;
  const { BaseLocation: _baseLocation, ...rest } = body;
  return rest;
}

// ---------------------------------------------------------------------------
// Service calls
// ---------------------------------------------------------------------------

/**
 * List every company under the current tenant. Auth is automatic via
 * `httpClient`'s Bearer-token interceptor — the tenant scope is derived
 * server-side from the JWT.
 */
export async function listEmployers(): Promise<Employer[]> {
  const { data } = await httpClient.get<EmployersListWire>(EMPLOYERS_URL);
  const wireList = Array.isArray(data) ? data : (data?.Items ?? data?.Employers ?? []);
  return wireList.map(normalizeEmployer);
}

/**
 * Fetch a single employer enriched with its module policies + settings.
 * `employerId` is the full EntityItemId (e.g. "Employer:xxx").
 */
export async function getEmployer(employerId: string): Promise<EmployerDetails> {
  if (!employerId) throw new Error('employerId is required');
  const { data } = await httpClient.get<EmployerDetailsWire>(
    `/employment/v1/employers/${seg(employerId)}`,
  );
  return normalizeEmployerDetails(data);
}

/** Create a new employer (Kind-aware). The BE sets `Type`. */
export async function createEmployer(body: EmployerRequest): Promise<Employer> {
  const { data } = await httpClient.post<EmployerWire>(
    EMPLOYERS_URL,
    stripUnsupportedRequestKeys(body),
  );
  return normalizeEmployer(data);
}

/** Update an existing employer. */
export async function updateEmployer(employerId: string, body: EmployerRequest): Promise<Employer> {
  if (!employerId) throw new Error('employerId is required');
  const { data } = await httpClient.put<EmployerWire>(
    `/employment/v1/employers/${seg(employerId)}`,
    stripUnsupportedRequestKeys(body),
  );
  return normalizeEmployer(data);
}

/**
 * Update one company policy / setting key.
 *
 * `PUT /employment/v1/employers/{employerId}/policies-settings/{policySettingId}` where
 * `policySettingId` is the module's `Id` (e.g. `Policy:ATT:xxx` /
 * `Setting:Localization:xxx`). The BE returns the strongly-consistent
 * post-update `{ Key, Value }` (DDB `UpdateItem ALL_NEW`) — callers apply that
 * directly into local state rather than re-fetching the eventually-consistent
 * GET.
 */
export async function updatePolicySetting(
  employerId: string,
  policySettingId: string,
  body: { Key: string; Value: unknown },
): Promise<{ key: string; value: unknown }> {
  if (!employerId) throw new Error('employerId is required');
  if (!policySettingId) throw new Error('policySettingId is required');
  const { data } = await httpClient.put<{ Key?: string; Value?: unknown }>(
    `/employment/v1/employers/${seg(employerId)}/policies-settings/${seg(policySettingId)}`,
    body,
  );
  return { key: data?.Key ?? body.Key, value: data?.Value };
}
